import { ActivatedRoute } from '@angular/router';
import { RecordingScheduleGanttComponent } from '../../../admin/components/management/recording-schedule-gantt/recording-schedule-gantt.component';
import { RecordingsComponent } from '../../../admin/components/management/recordings/recordings.component';
import { SocialDossiersComponent } from '../../../admin/components/social/social-dossiers/social-dossiers.component';
import { SocialSettingsComponent } from '../../../admin/components/social/social-settings/social-settings.component';
import { ClipAutoGroupingSettingsComponent } from '../../../admin/components/clip-auto-grouping-settings/clip-auto-grouping-settings.component';
import { Component } from '@angular/core';
import { CommonModule } from '@angular/common';
import { TranslateModule } from '@ngx-translate/core';
import { MatCardModule } from '@angular/material/card';
import { MatIconModule } from '@angular/material/icon';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatButtonModule } from '@angular/material/button';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MAT_DATE_FORMATS, MAT_NATIVE_DATE_FORMATS, MatNativeDateModule } from '@angular/material/core';
import { FormsModule, ReactiveFormsModule, FormBuilder, FormGroup, Validators, AbstractControl, ValidationErrors } from '@angular/forms';
import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { ClientAccountControllerService, ClientChangePasswordRequest } from 'api-auth';
import { take } from 'rxjs/operators';
import { forkJoin, Observable } from 'rxjs';
import { SharedService } from '../../services/shared.service';
import { TranslateService } from '@ngx-translate/core';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatTooltipModule } from '@angular/material/tooltip';
import { AuthenticationService } from '../../services/authentication.service';
import { ClientControllerService, ManagementClientResponse, ManagementClientSubscriptionResponse, ClientSubscriptionControllerService } from 'api-client';
import { EditSubscriptionDialogComponent } from './edit-subscription-dialog/edit-subscription-dialog.component';
import { AiPresetDialogComponent } from './ai-preset-dialog/ai-preset-dialog.component';
import { AiPriceDialogComponent } from './ai-price-dialog/ai-price-dialog.component';
import { YoutubeSourceDialogComponent } from './youtube-source-dialog/youtube-source-dialog.component';
import { YoutubeSourcePreviewDialogComponent } from './youtube-source-preview-dialog/youtube-source-preview-dialog.component';
import { YoutubeDownloadLogComponent } from './youtube-download-log/youtube-download-log.component';
import { ConfirmationDialogComponent } from '../confirmation-dialog/confirmation-dialog.component';
import { MatTableModule, MatTableDataSource } from '@angular/material/table';
import { MatSlideToggleModule } from '@angular/material/slide-toggle';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatChipInputEvent, MatChipsModule } from '@angular/material/chips';
import { ENTER } from '@angular/cdk/keycodes';
import { MatDialog, MatDialogModule } from '@angular/material/dialog';
import { Constants } from '../../interfaces/constants';
import { ManagementAiPresetControllerService, ManagementAiPresetRequest, ManagementAiPresetResponse, ManagementAiPriceControllerService, ManagementAiPriceResponse } from 'api-ai-hub';
import { YoutubeSourceRequest, YoutubeSourceResponse, YoutubeSourceService } from '../../services/youtube-source.service';
import { environment } from '../../../../environments/environment';

interface SettingSection {
	id: string;
	title: string;
	icon: string;
}

@Component({
	selector: 'app-settings',
	standalone: true,
	imports: [
		CommonModule,
		TranslateModule,
		MatCardModule,
		MatIconModule,
		MatFormFieldModule,
		MatInputModule,
		MatButtonModule,
		MatProgressSpinnerModule,
		MatTooltipModule,
		MatTableModule,
		MatSlideToggleModule,
		MatCheckboxModule,
		MatChipsModule,
		YoutubeDownloadLogComponent,
		RecordingScheduleGanttComponent,
		RecordingsComponent,
		SocialDossiersComponent,
		SocialSettingsComponent,
		ClipAutoGroupingSettingsComponent,
		MatDialogModule,
		MatDatepickerModule,
		MatNativeDateModule,
		ReactiveFormsModule,
		FormsModule
	],
	providers: [
		{ provide: MAT_DATE_FORMATS, useValue: MAT_NATIVE_DATE_FORMATS }
	],
	templateUrl: './settings.component.html',
	styleUrl: './settings.component.scss'
})

export class SettingsComponent {
	isAdmin = false;
	activeSection = 'subscriptions';
	changePasswordForm: FormGroup;
	isChangingPassword = false;
	oldPasswordVisible = false;
	newPasswordVisible = false;
	confirmPasswordVisible = false;

	clientData: ManagementClientResponse | null = null;
	displayedColumnsSubscription: string[] = ['TYPE', 'EMAIL_TO', 'EMAIL_CC', 'ACTIONS'];
	dataSourceSubscription: MatTableDataSource<ManagementClientSubscriptionResponse> = new MatTableDataSource<ManagementClientSubscriptionResponse>();
	subscriptionTypes = Constants.CLIENT_SUBSCRIPTION_TYPES;
	isLoadingSubscriptions = false;

	displayedColumnsAiPresets: string[] = ['TYPE', 'PROVIDER', 'MODEL', 'ACTIONS'];
	dataSourceAiPresets: MatTableDataSource<ManagementAiPresetResponse> = new MatTableDataSource<ManagementAiPresetResponse>();
	isLoadingAiPresets = false;
	private allPresetTypes = (Object.values(ManagementAiPresetRequest.TypeEnum) as ManagementAiPresetRequest.TypeEnum[])
		.sort((a, b) => a.localeCompare(b));

	displayedColumnsAiPrices: string[] = ['PROVIDER', 'MODEL', 'PRICE_PER_MILLION_INPUT_TOKENS', 'PRICE_PER_MILLION_OUTPUT_TOKENS', 'PRICE_PER_MILLION_CACHED_INPUT_TOKENS', 'EFFECTIVE_FROM', 'ACTIONS'];
	dataSourceAiPrices: MatTableDataSource<ManagementAiPriceResponse> = new MatTableDataSource<ManagementAiPriceResponse>();
	isLoadingAiPrices = false;

	displayedColumnsYoutubeSources: string[] = ['SELECT', 'NAME', 'TYPE', 'KEYWORDS', 'LAST_CHECKED_AT', 'SCHEDULE', 'ENABLED', 'ACTIONS'];
	selectedYoutubeSourceIds = new Set<string>();
	isBatchDownloadingYoutubeSources = false;
	dataSourceYoutubeSources: MatTableDataSource<YoutubeSourceResponse> = new MatTableDataSource<YoutubeSourceResponse>();
	isLoadingYoutubeSources = false;
	/** Enabled-toggle changes staged locally until "Confirm" - keyed by source id.
	 *  Nothing is sent to the server until confirmYoutubeEnabledChanges() runs. */
	pendingYoutubeEnabled = new Map<string, boolean>();
	isSavingYoutubeEnabledChanges = false;

	private dialogRefOpen: boolean = false;

	settingSections: SettingSection[] = [];

	private readonly clientSections: SettingSection[] = [
		{ id: 'subscriptions', title: 'CLIENT_SETTINGS.SUBSCRIPTIONS', icon: 'subscriptions' },
		{ id: 'password', title: 'CLIENT_SETTINGS.CHANGE_PASSWORD', icon: 'lock' }
	];

	private readonly adminSections: SettingSection[] = [
		{ id: 'ai-presets', title: 'SETTINGS.AI_PRESETS', icon: 'smart_toy' },
		{ id: 'ai-prices', title: 'SETTINGS.AI_PRICES', icon: 'payments' },
		{ id: 'ai-usage-export', title: 'SETTINGS.AI_USAGE_EXPORT.TITLE', icon: 'download' },
		{ id: 'youtube-sources', title: 'SETTINGS.YOUTUBE_SOURCES', icon: 'smart_display' },
		{ id: 'youtube-download-log', title: 'SETTINGS.YOUTUBE_DOWNLOAD_LOG.PAGE_TITLE', icon: 'history' },
		{ id: 'recording-schedules', title: 'NAVBAR.RECORDING_SCHEDULES', icon: 'schedule' },
		{ id: 'recordings', title: 'NAVBAR.RECORDINGS', icon: 'videocam' },
		// docs/SOCIJALNI-FAZA1-EKRAN.md section 1: Dosieja + Postavki moved here, out of the
		// admin/social tab group, as their own "Социјални мрежи" part of this screen.
		{ id: 'social-dossiers', title: 'SETTINGS.SOCIAL.DOSSIERS', icon: 'folder_shared' },
		{ id: 'social-settings', title: 'SETTINGS.SOCIAL.SETTINGS', icon: 'tune' },
		// Faza 1, feature/clip-auto-grouping.
		{ id: 'clip-auto-grouping', title: 'SETTINGS.CLIP_AUTO_GROUPING', icon: 'merge_type' }
	];

	private readonly adminSectionScopes: Record<string, string> = {
		'ai-presets': 'mgmt:ai',
		'ai-prices': 'mgmt:ai',
		'ai-usage-export': 'mgmt:ai',
		'youtube-sources': 'mgmt:transcriptions',
		'youtube-download-log': 'mgmt:transcriptions',
		'recording-schedules': 'mgmt:other',
		'recordings': 'mgmt:other',
		// Same scope as the admin/social nav entry (Constants.COMPONENTS_TREE "SOCIAL_POSTS").
		'social-dossiers': 'mgmt:other',
		'social-settings': 'mgmt:other',
		'clip-auto-grouping': 'mgmt:clipping'
	};

	readonly usageExportMaxRangeMonths = 3;
	usageExportForm: FormGroup;
	isExportingUsage = false;

	constructor(
		private fb: FormBuilder,
		private clientAccountService: ClientAccountControllerService,
		private sharedService: SharedService,
		private translate: TranslateService,
		private authService: AuthenticationService,
		private clientService: ClientControllerService,
		private clientSubscriptionService: ClientSubscriptionControllerService,
		private dialog: MatDialog,
		private apiAiPreset: ManagementAiPresetControllerService,
		private apiAiPrice: ManagementAiPriceControllerService,
		private apiYoutubeSource: YoutubeSourceService,
		private http: HttpClient,
		private route: ActivatedRoute
	) {
		this.changePasswordForm = this.fb.group({
			oldPassword: ['', [Validators.required]],
			newPassword: ['', [Validators.required]],
			confirmPassword: ['', [Validators.required]]
		}, { validators: this.passwordMatchValidator });

		const usageExportDateTo = new Date();
		const usageExportDateFrom = new Date(usageExportDateTo);
		usageExportDateFrom.setDate(usageExportDateFrom.getDate() - 30);
		this.usageExportForm = this.fb.group({
			dateFrom: [usageExportDateFrom, [Validators.required]],
			dateTo: [usageExportDateTo, [Validators.required]]
		}, { validators: this.usageExportDateRangeValidator.bind(this) });

		this.isAdmin = this.authService.loginData.getValue()?.role === 'MGMT';
		this.initSettingSections();
		// ?section=recordings opens that section directly (old links such as /admin/recordings
		// redirect here).
		const requested = this.route.snapshot.queryParamMap.get('section');
		if (requested && this.settingSections.some(section => section.id === requested)) {
			this.activeSection = requested;
		}

		if (!this.isAdmin) {
			if (this.activeSection === 'subscriptions' && this.hasScope('client:subscriptions')) {
				setTimeout(() => {
					this.loadClientData();
				}, 100);
			} else if (this.activeSection === 'subscriptions' && !this.hasScope('client:subscriptions')) {
				this.activeSection = 'password';
			}
		} else if (this.activeSection === 'ai-presets') {
			this.loadAiPresets();
		} else if (this.activeSection === 'ai-prices') {
			this.loadAiPrices();
		} else if (this.activeSection === 'youtube-sources') {
			this.loadYoutubeSources();
		}
	}

	selectSection(sectionId: string): void {
		// Leaving the section discards any staged-but-unconfirmed toggle, same as Cancel.
		this.pendingYoutubeEnabled.clear();
		this.activeSection = sectionId;
		if (sectionId === 'subscriptions' && this.hasScope('client:subscriptions')) {
			this.loadClientData();
		} else if (sectionId === 'ai-presets') {
			this.loadAiPresets();
		} else if (sectionId === 'ai-prices') {
			this.loadAiPrices();
		} else if (sectionId === 'youtube-sources') {
			this.loadYoutubeSources();
		}
	}

	getActiveSection(): SettingSection | undefined {
		return this.settingSections.find(section => section.id === this.activeSection);
	}

	// The "Качи снимка" button lives here (same line as the section title) rather than inside
	// RecordingsComponent's own template, which has no header at all in embedded mode. Reaches it
	// through the same cross-component action channel ClipsFormComponent already listens on.
	openUploadRecordingDialog(): void {
		this.sharedService.requestActionInComponent.next({ componentName: 'RecordingsComponent', action: 'OPEN_UPLOAD_DIALOG' });
	}

	passwordMatchValidator(group: AbstractControl) {
		const newPassword = group.get('newPassword')?.value;
		const confirmPassword = group.get('confirmPassword')?.value;

		if (newPassword && confirmPassword && newPassword !== confirmPassword) {
			group.get('confirmPassword')?.setErrors({ passwordMismatch: true });
			return { passwordMismatch: true };
		} else {
			const confirmControl = group.get('confirmPassword');
			if (confirmControl?.hasError('passwordMismatch')) {
				const errors = { ...confirmControl.errors };
				delete errors['passwordMismatch'];
				confirmControl.setErrors(Object.keys(errors).length ? errors : null);
			}
		}
		return null;
	}

	onChangePassword(): void {
		if (this.changePasswordForm.valid && !this.isChangingPassword) {
			this.isChangingPassword = true;

			const request: ClientChangePasswordRequest = {
				oldPassword: this.changePasswordForm.value.oldPassword,
				newPassword: this.changePasswordForm.value.newPassword
			};

			this.clientAccountService
			.change(request)
			.pipe(take(1))
			.subscribe({
				next: () => {
					this.isChangingPassword = false;
					this.changePasswordForm.reset();
					this.changePasswordForm.markAsUntouched();
					this.changePasswordForm.markAsPristine();
					Object.keys(this.changePasswordForm.controls).forEach(key => {
						this.changePasswordForm.get(key)?.setErrors(null);
					});
					this.sharedService.showNotification.next({
						message: this.translate.instant('NOTIFICATIONS.RECORD_UPDATED'),
						type: 'SUCCESS'
					});
					setTimeout(() => {
						this.authService.userLogout('Password changed');
					}, 500);
				},
				error: () => {
					this.isChangingPassword = false;
					this.sharedService.showNotification.next({
						message: this.translate.instant('NOTIFICATIONS.RECORD_ACTION_ERROR'),
						type: 'ERROR'
					});
				}
			});
		}
	}

	private loadClientData(): void {
		if (!this.clientData) {
			this.clientService
				.getClientDetails()
				.pipe(take(1))
				.subscribe({
					next: (response) => {
						this.clientData = response;
						this.loadSubscriptions();
					},
					error: (error) => {
						this.sharedService.showNotification.next({
							message: this.translate.instant('NOTIFICATIONS.RECORD_ACTION_ERROR'),
							type: 'ERROR'
						});
					}
				});
		} else {
			this.loadSubscriptions();
		}
	}

	private loadSubscriptions(): void {
		this.isLoadingSubscriptions = true;
		this.clientSubscriptionService
			.getClientSubscriptions()
			.pipe(take(1))
			.subscribe({
				next: (response) => {
					this.dataSourceSubscription.data = response;
					this.isLoadingSubscriptions = false;
				},
				error: (error) => {
					this.isLoadingSubscriptions = false;
					this.sharedService.showNotification.next({
						message: this.translate.instant('NOTIFICATIONS.RECORD_ACTION_ERROR'),
						type: 'ERROR'
					});
				}
			});
	}

	getSubscriptionValidityStatus(validTo: Date) {
		const currentDate = new Date();
		if (validTo) {
			if (new Date(validTo) < currentDate) {
				return 'red';
			}
			if (new Date(validTo) < new Date(currentDate.setMonth(currentDate.getMonth() + 1))) {
				return 'orange';
			}
		}
		return '#1976d2';
	}

	private initSettingSections(): void {
		if (this.isAdmin) {
			this.settingSections = this.adminSections.filter(section => {
				const requiredScope = this.adminSectionScopes[section.id];
				return !requiredScope || this.hasScope(requiredScope);
			});
			this.activeSection = this.settingSections[0]?.id;
		} else {
			this.settingSections = this.clientSections.filter(section => {
				if (section.id === 'subscriptions') {
					return this.hasScope('client:subscriptions');
				}
				return true;
			});
		}
	}

	hasScope(scope: string): boolean {
		const loginData = this.authService.loginData.getValue();
		return loginData?.scopes?.includes(scope) || false;
	}

	subscriptionAction(_action: 'EDIT', subscription: ManagementClientSubscriptionResponse): void {
		if (!this.clientData?.id || this.dialogRefOpen) return;

		this.dialogRefOpen = true;
		const dialogRef = this.dialog.open(EditSubscriptionDialogComponent, {
			width: '800px',
			data: {
				subscription,
				clientId: this.clientData.id,
				clientData: this.clientData
			}
		});

		dialogRef.afterClosed().pipe(take(1)).subscribe(result => {
			this.dialogRefOpen = false;
			if (result) {
				this.loadSubscriptions();
			}
		});
	}

	isSubscriptionExpired(validTo: Date | undefined): boolean {
		if (!validTo) {
			return false;
		}
		return new Date(validTo) < new Date();
	}

	loadAiPresets(): void {
		this.isLoadingAiPresets = true;
		this.apiAiPreset
			.getAiPresets()
			.pipe(take(1))
			.subscribe({
				next: response => {
					this.dataSourceAiPresets.data = (response || []).sort((a, b) => (a.type ?? '').localeCompare(b.type ?? ''));
					this.isLoadingAiPresets = false;
				},
				error: () => {
					this.isLoadingAiPresets = false;
					this.sharedService.showNotification.next({
						message: this.translate.instant('NOTIFICATIONS.RECORD_ACTION_ERROR'),
						type: 'ERROR',
					});
				},
			});
	}

	private availablePresetTypes(currentType?: ManagementAiPresetRequest.TypeEnum): ManagementAiPresetRequest.TypeEnum[] {
		const usedTypes = new Set(this.dataSourceAiPresets.data.map(p => p.type).filter((t): t is ManagementAiPresetRequest.TypeEnum => !!t));
		if (currentType) usedTypes.delete(currentType);
		return this.allPresetTypes.filter(t => !usedTypes.has(t));
	}

	openAiPresetDialog(preset?: ManagementAiPresetResponse): void {
		if (this.dialogRefOpen) return;
		this.dialogRefOpen = true;
		const dialogRef = this.dialog.open(AiPresetDialogComponent, {
			width: '900px',
			maxWidth: '95vw',
			data: {
				preset,
				availableTypes: this.availablePresetTypes(preset?.type),
			},
		});

		dialogRef.afterClosed().pipe(take(1)).subscribe(result => {
			this.dialogRefOpen = false;
			if (result) this.loadAiPresets();
		});
	}

	deleteAiPreset(preset: ManagementAiPresetResponse, event: Event): void {
		event.stopPropagation();
		if (!preset.id) return;
		const dialogRef = this.dialog.open(ConfirmationDialogComponent, {
			width: '600px',
			data: {
				type: 'delete-confirmation',
				mode: 'DELETE',
			},
		});

		dialogRef.afterClosed().pipe(take(1)).subscribe(result => {
			if (!result || !preset.id) return;
			this.apiAiPreset
				.deleteAiPreset(preset.id)
				.pipe(take(1))
				.subscribe({
					next: () => {
						this.sharedService.showNotification.next({
							message: this.translate.instant('NOTIFICATIONS.RECORD_DELETED'),
							type: 'SUCCESS',
						});
						this.loadAiPresets();
					},
					error: () => {
						this.sharedService.showNotification.next({
							message: this.translate.instant('NOTIFICATIONS.RECORD_ACTION_ERROR'),
							type: 'ERROR',
						});
					},
				});
		});
	}

	canAddAiPreset(): boolean {
		return this.availablePresetTypes().length > 0;
	}

	loadYoutubeSources(): void {
		this.isLoadingYoutubeSources = true;
		this.apiYoutubeSource
			.getSources()
			.pipe(take(1))
			.subscribe({
				next: response => {
					this.dataSourceYoutubeSources.data = response || [];
					this.isLoadingYoutubeSources = false;
				},
				error: () => {
					this.isLoadingYoutubeSources = false;
					this.sharedService.showNotification.next({
						message: this.translate.instant('NOTIFICATIONS.RECORD_ACTION_ERROR'),
						type: 'ERROR',
					});
				},
			});
		this.loadYoutubeAutoDownloadSettings();
	}

	youtubeAutoDownloadEnabled = false;
	youtubeGeneralScheduleTimes: string[] = [];
	isSavingYoutubeAutoDownloadSettings = false;
	readonly scheduleTimeSeparatorKeyCodes: number[] = [ENTER];

	private loadYoutubeAutoDownloadSettings(): void {
		this.apiYoutubeSource
			.getSettings()
			.pipe(take(1))
			.subscribe({
				next: settings => {
					this.youtubeAutoDownloadEnabled = settings.enabled;
					this.youtubeGeneralScheduleTimes = (settings.generalScheduleTimes ?? '')
						.split(',')
						.map(value => value.trim())
						.filter(value => !!value);
				},
			});
	}

	addGeneralScheduleTime(event: MatChipInputEvent): void {
		const value = (event.value || '').trim();
		if (value && !this.youtubeGeneralScheduleTimes.includes(value)) {
			this.youtubeGeneralScheduleTimes.push(value);
		}
		event.chipInput.clear();
	}

	removeGeneralScheduleTime(value: string): void {
		this.youtubeGeneralScheduleTimes = this.youtubeGeneralScheduleTimes.filter(item => item !== value);
	}

	saveYoutubeAutoDownloadSettings(): void {
		if (this.isSavingYoutubeAutoDownloadSettings) return;
		this.isSavingYoutubeAutoDownloadSettings = true;

		this.apiYoutubeSource
			.updateSettings({
				enabled: this.youtubeAutoDownloadEnabled,
				generalScheduleTimes: this.youtubeGeneralScheduleTimes.length ? this.youtubeGeneralScheduleTimes.join(',') : null,
			})
			.pipe(take(1))
			.subscribe({
				next: () => {
					this.isSavingYoutubeAutoDownloadSettings = false;
					this.sharedService.showNotification.next({
						message: this.translate.instant('NOTIFICATIONS.RECORD_UPDATED'),
						type: 'SUCCESS',
					});
					this.loadYoutubeSources();
				},
				error: err => {
					this.isSavingYoutubeAutoDownloadSettings = false;
					this.sharedService.showNotification.next({
						message: `${this.translate.instant('NOTIFICATIONS.RECORD_ACTION_ERROR')} \n ${err?.error?.message ?? ''}`,
						type: 'ERROR',
					});
				},
			});
	}

	/** Stages the toggle locally instead of saving it - see confirmYoutubeEnabledChanges/
	 *  cancelYoutubeEnabledChanges. Toggling back to the original value un-stages it. */
	isYoutubeSourceEnabledChecked(source: YoutubeSourceResponse): boolean {
		return this.pendingYoutubeEnabled.has(source.id) ? !!this.pendingYoutubeEnabled.get(source.id) : source.enabled;
	}

	stageYoutubeSourceEnabledChange(source: YoutubeSourceResponse): void {
		const nextValue = !this.isYoutubeSourceEnabledChecked(source);
		if (nextValue === source.enabled) {
			this.pendingYoutubeEnabled.delete(source.id);
		} else {
			this.pendingYoutubeEnabled.set(source.id, nextValue);
		}
	}

	cancelYoutubeEnabledChanges(): void {
		this.pendingYoutubeEnabled.clear();
	}

	confirmYoutubeEnabledChanges(): void {
		if (this.pendingYoutubeEnabled.size === 0 || this.isSavingYoutubeEnabledChanges) return;
		this.isSavingYoutubeEnabledChanges = true;

		const sources = this.dataSourceYoutubeSources.data;
		const updates: Observable<YoutubeSourceResponse>[] = [];
		this.pendingYoutubeEnabled.forEach((enabled, id) => {
			const source = sources.find(s => s.id === id);
			if (!source) return;
			const body: YoutubeSourceRequest = {
				name: source.name,
				url: source.url,
				keywords: source.keywords,
				enabled,
				customScheduleTimes: source.customScheduleTimes,
			};
			updates.push(this.apiYoutubeSource.updateSource(id, body));
		});

		if (updates.length === 0) {
			this.pendingYoutubeEnabled.clear();
			this.isSavingYoutubeEnabledChanges = false;
			return;
		}

		forkJoin(updates)
			.pipe(take(1))
			.subscribe({
				next: () => {
					this.pendingYoutubeEnabled.clear();
					this.isSavingYoutubeEnabledChanges = false;
					this.sharedService.showNotification.next({
						message: this.translate.instant('NOTIFICATIONS.RECORD_UPDATED'),
						type: 'SUCCESS',
					});
					this.loadYoutubeSources();
				},
				error: () => {
					this.isSavingYoutubeEnabledChanges = false;
					this.sharedService.showNotification.next({
						message: this.translate.instant('NOTIFICATIONS.RECORD_ACTION_ERROR'),
						type: 'ERROR',
					});
				},
			});
	}

	openYoutubeSourceDialog(source?: YoutubeSourceResponse): void {
		if (this.dialogRefOpen) return;
		this.dialogRefOpen = true;
		const dialogRef = this.dialog.open(YoutubeSourceDialogComponent, {
			width: '700px',
			maxWidth: '95vw',
			data: { source },
		});

		dialogRef.afterClosed().pipe(take(1)).subscribe(result => {
			this.dialogRefOpen = false;
			if (result) this.loadYoutubeSources();
		});
	}

	downloadingYoutubeSourceIds = new Set<string>();

	downloadYoutubeSourceNow(source: YoutubeSourceResponse, event: Event): void {
		event.stopPropagation();
		if (this.downloadingYoutubeSourceIds.has(source.id)) return;
		this.downloadingYoutubeSourceIds.add(source.id);

		this.apiYoutubeSource
			.downloadSource(source.id)
			.pipe(take(1))
			.subscribe({
				next: response => {
					this.downloadingYoutubeSourceIds.delete(source.id);
					this.sharedService.showNotification.next({
						message: this.translate.instant('SETTINGS.YOUTUBE_SOURCE.DOWNLOAD.DONE', { count: response.downloadedCount })
							+ (response.truncated ? ' ' + this.translate.instant('SETTINGS.YOUTUBE_SOURCE.DOWNLOAD.TRUNCATED') : ''),
						type: 'SUCCESS',
					});
					this.loadYoutubeSources();
				},
				error: err => {
					this.downloadingYoutubeSourceIds.delete(source.id);
					this.sharedService.showNotification.next({
						message: `${this.translate.instant('NOTIFICATIONS.RECORD_ACTION_ERROR')} \n ${err?.error?.message ?? ''}`,
						type: 'ERROR',
					});
				},
			});
	}

	toggleYoutubeSourceSelected(source: YoutubeSourceResponse): void {
		if (this.selectedYoutubeSourceIds.has(source.id)) {
			this.selectedYoutubeSourceIds.delete(source.id);
		} else {
			this.selectedYoutubeSourceIds.add(source.id);
		}
	}

	downloadSelectedYoutubeSourcesNow(): void {
		if (this.selectedYoutubeSourceIds.size === 0 || this.isBatchDownloadingYoutubeSources) return;
		this.isBatchDownloadingYoutubeSources = true;

		this.apiYoutubeSource
			.downloadSourcesBatch(Array.from(this.selectedYoutubeSourceIds))
			.pipe(take(1))
			.subscribe({
				next: () => {
					this.isBatchDownloadingYoutubeSources = false;
					this.selectedYoutubeSourceIds.clear();
					this.sharedService.showNotification.next({
						message: this.translate.instant('SETTINGS.YOUTUBE_SOURCE.DOWNLOAD.BATCH_STARTED'),
						type: 'SUCCESS',
					});
				},
				error: err => {
					this.isBatchDownloadingYoutubeSources = false;
					this.sharedService.showNotification.next({
						message: `${this.translate.instant('NOTIFICATIONS.RECORD_ACTION_ERROR')} \n ${err?.error?.message ?? ''}`,
						type: 'ERROR',
					});
				},
			});
	}

	openYoutubeSourcePreviewDialog(source: YoutubeSourceResponse, event: Event): void {
		event.stopPropagation();
		if (this.dialogRefOpen) return;
		this.dialogRefOpen = true;
		const dialogRef = this.dialog.open(YoutubeSourcePreviewDialogComponent, {
			width: '800px',
			maxWidth: '95vw',
			data: { source },
		});

		dialogRef.afterClosed().pipe(take(1)).subscribe(() => {
			this.dialogRefOpen = false;
		});
	}

	deleteYoutubeSource(source: YoutubeSourceResponse, event: Event): void {
		event.stopPropagation();
		if (!source.id) return;
		const dialogRef = this.dialog.open(ConfirmationDialogComponent, {
			width: '600px',
			data: {
				type: 'delete-confirmation',
				mode: 'DELETE',
			},
		});

		dialogRef.afterClosed().pipe(take(1)).subscribe(result => {
			if (!result || !source.id) return;
			this.apiYoutubeSource
				.deleteSource(source.id)
				.pipe(take(1))
				.subscribe({
					next: () => {
						this.sharedService.showNotification.next({
							message: this.translate.instant('NOTIFICATIONS.RECORD_DELETED'),
							type: 'SUCCESS',
						});
						this.loadYoutubeSources();
					},
					error: () => {
						this.sharedService.showNotification.next({
							message: this.translate.instant('NOTIFICATIONS.RECORD_ACTION_ERROR'),
							type: 'ERROR',
						});
					},
				});
		});
	}

	loadAiPrices(): void {
		this.isLoadingAiPrices = true;
		this.apiAiPrice
			.getAiPrices()
			.pipe(take(1))
			.subscribe({
				next: response => {
					this.dataSourceAiPrices.data = (response || []).sort((a, b) => {
						const providerCompare = (a.provider ?? '').localeCompare(b.provider ?? '');
						if (providerCompare !== 0) return providerCompare;
						const modelCompare = (a.model ?? '').localeCompare(b.model ?? '');
						if (modelCompare !== 0) return modelCompare;
						return new Date(b.effectiveFrom ?? 0).getTime() - new Date(a.effectiveFrom ?? 0).getTime();
					});
					this.isLoadingAiPrices = false;
				},
				error: () => {
					this.isLoadingAiPrices = false;
					this.sharedService.showNotification.next({
						message: this.translate.instant('NOTIFICATIONS.RECORD_ACTION_ERROR'),
						type: 'ERROR',
					});
				},
			});
	}

	openAiPriceDialog(price?: ManagementAiPriceResponse): void {
		if (this.dialogRefOpen) return;
		this.dialogRefOpen = true;
		const dialogRef = this.dialog.open(AiPriceDialogComponent, {
			width: '600px',
			maxWidth: '95vw',
			data: { price },
		});

		dialogRef.afterClosed().pipe(take(1)).subscribe(result => {
			this.dialogRefOpen = false;
			if (result) this.loadAiPrices();
		});
	}

	deleteAiPrice(price: ManagementAiPriceResponse, event: Event): void {
		event.stopPropagation();
		if (!price.id) return;
		const dialogRef = this.dialog.open(ConfirmationDialogComponent, {
			width: '600px',
			data: {
				type: 'delete-confirmation',
				mode: 'DELETE',
			},
		});

		dialogRef.afterClosed().pipe(take(1)).subscribe(result => {
			if (!result || !price.id) return;
			this.apiAiPrice
				.deleteAiPrice(price.id)
				.pipe(take(1))
				.subscribe({
					next: () => {
						this.sharedService.showNotification.next({
							message: this.translate.instant('NOTIFICATIONS.RECORD_DELETED'),
							type: 'SUCCESS',
						});
						this.loadAiPrices();
					},
					error: () => {
						this.sharedService.showNotification.next({
							message: this.translate.instant('NOTIFICATIONS.RECORD_ACTION_ERROR'),
							type: 'ERROR',
						});
					},
				});
		});
	}

	private addMonths(date: Date, months: number): Date {
		const targetMonthIndex = date.getMonth() + months;
		const lastDayOfTargetMonth = new Date(date.getFullYear(), targetMonthIndex + 1, 0).getDate();
		return new Date(date.getFullYear(), targetMonthIndex, Math.min(date.getDate(), lastDayOfTargetMonth));
	}

	private usageExportDateRangeValidator(group: AbstractControl): ValidationErrors | null {
		const dateFrom = group.get('dateFrom')?.value;
		const dateTo = group.get('dateTo')?.value;
		if (!dateFrom || !dateTo) {
			return null;
		}
		if (new Date(dateFrom) > new Date(dateTo)) {
			return { rangeInvalid: true };
		}
		if (new Date(dateTo) > this.addMonths(new Date(dateFrom), this.usageExportMaxRangeMonths)) {
			return { rangeTooLong: true };
		}
		return null;
	}

	getUsageExportMaxDateTo(): Date | null {
		const dateFrom = this.usageExportForm.get('dateFrom')?.value;
		return dateFrom ? this.addMonths(new Date(dateFrom), this.usageExportMaxRangeMonths) : null;
	}

	exportUsage(): void {
		if (this.usageExportForm.invalid || this.isExportingUsage) {
			return;
		}

		const dateFrom: Date = this.usageExportForm.value.dateFrom;
		const dateTo: Date = this.usageExportForm.value.dateTo;
		const dateFromIso = this.startOfDayIso(dateFrom);
		const dateToIso = this.endOfDayIso(dateTo);

		this.isExportingUsage = true;
		// Called via HttpClient directly (not the generated api-ai-hub SDK): the SDK has not been
		// regenerated with the /v1/management/usage/export endpoint yet. Switch this to
		// ManagementAiUsageControllerService.exportUsageRecords() once it has been.
		this.http
			.get(`${environment.apiAiHubUrl}/v1/management/usage/export`, {
				params: { dateFrom: dateFromIso, dateTo: dateToIso },
				responseType: 'blob'
			})
			.pipe(take(1))
			.subscribe({
				next: response => {
					this.isExportingUsage = false;
					this.downloadUsageExport(response, dateFrom, dateTo);
				},
				error: (error: HttpErrorResponse) => {
					this.handleUsageExportError(error);
				}
			});
	}

	private startOfDayIso(date: Date): string {
		const result = new Date(date);
		result.setHours(0, 0, 0, 0);
		return result.toISOString();
	}

	private endOfDayIso(date: Date): string {
		const result = new Date(date);
		result.setHours(23, 59, 59, 999);
		return result.toISOString();
	}

	private downloadUsageExport(response: unknown, dateFrom: Date, dateTo: Date): void {
		const content = new Blob([response as BlobPart], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
		const fileURL = URL.createObjectURL(content);
		const link = document.createElement('a');
		link.href = fileURL;
		link.download = `ai-usage-${this.formatDateForFilename(dateFrom)}-${this.formatDateForFilename(dateTo)}.xlsx`;
		link.click();
		URL.revokeObjectURL(fileURL);
	}

	private handleUsageExportError(error: HttpErrorResponse): void {
		this.isExportingUsage = false;
		if (error.status === 400) {
			this.sharedService.showNotification.next({
				message: this.translate.instant('SETTINGS.AI_USAGE_EXPORT.INVALID_RANGE'),
				type: 'ERROR',
			});
		} else {
			this.sharedService.showNotification.next({
				message: this.translate.instant('NOTIFICATIONS.RECORD_ACTION_ERROR'),
				type: 'ERROR',
			});
		}
	}

	private formatDateForFilename(date: Date): string {
		const year = date.getFullYear();
		const month = String(date.getMonth() + 1).padStart(2, '0');
		const day = String(date.getDate()).padStart(2, '0');
		return `${year}-${month}-${day}`;
	}
}
